import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Upload, Sparkles, Loader2, Wand2, ArrowRight, Check, ImagePlus, Palette, Share2, Download } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import { api, imgUrl, track, SITE_URL } from "@/lib/api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Vizualizacia() {
  const [palettes, setPalettes] = useState([]);
  const [swatchUrl, setSwatchUrl] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [palette, setPalette] = useState("");
  const [colors, setColors] = useState("");
  const [objectType, setObjectType] = useState("dom");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    document.title = "Bezplatná AI vizualizácia fasády | Zateplenia.eu";
    track("visit", "visualizacia");
    api.get("/visualize/config").then((r) => {
      setPalettes(r.data.palettes || []);
      setSwatchUrl(r.data.swatch_url || "");
    }).catch(() => {});
  }, []);

  const pickFile = (f) => {
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
      return toast.error("Nahrajte fotku vo formáte JPG, PNG alebo WEBP.");
    }
    if (f.size > 10 * 1024 * 1024) return toast.error("Fotka je príliš veľká (max 10 MB).");
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const generate = async () => {
    if (!file) return toast.error("Najprv nahrajte fotku domu alebo bytovky.");
    if (!EMAIL_RE.test(email)) return toast.error("Zadajte platný e-mail, na ktorý pošleme návrhy.");
    if (!palette && !colors.trim()) return toast.error("Vyberte paletu alebo napíšte želané farby.");
    setBusy(true);
    track("visualize_submit", palette || "custom");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("email", email);
      fd.append("name", name);
      fd.append("colors", colors);
      fd.append("palette", palette);
      fd.append("object_type", objectType);
      fd.append("gallery_consent", consent ? "true" : "false");
      const { data } = await api.post("/visualize", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success("Hotovo! Vaše 3 návrhy sú pripravené.");
      window.__lenis?.scrollTo(0, { immediate: true }) || window.scrollTo(0, 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Vizualizáciu sa nepodarilo vytvoriť. Skúste znova.");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setResult(null);
    setFile(null);
    setPreview("");
    setColors("");
    setPalette("");
  };

  return (
    <div>
      <Header />

      {/* Hero */}
      <section className="relative pt-36 pb-16 overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img src="https://images.pexels.com/photos/1396122/pexels-photo-1396122.jpeg?auto=compress&cs=tinysrgb&w=1600" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/90 to-ink/60" />
        </div>
        <div className="relative z-10 max-w-[1100px] mx-auto px-5 md:px-10">
          <div className="inline-flex items-center gap-2 text-gold font-mono2 uppercase tracking-[0.3em] text-xs mb-6">
            <Sparkles className="w-4 h-4" /> Zadarmo · AI vizualizácia
          </div>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="font-display text-white leading-[0.9] text-4xl sm:text-5xl lg:text-6xl max-w-4xl" data-testid="vis-title">
            Ako bude vyzerať<br /><span className="text-gold">vaša nová fasáda?</span>
          </motion.h1>
          <p className="text-white/70 text-base md:text-lg max-w-2xl mt-6">
            Nahrajte fotku domu alebo bytovky, vyberte farby a naša AI vám vytvorí
            <strong className="text-white"> 3 realistické návrhy zateplenej fasády — úplne zadarmo.</strong>
          </p>
        </div>
      </section>

      {result ? (
        <ResultView result={result} name={name} email={email} colors={colors} palette={palette} onReset={reset} />
      ) : (
        <section className="bg-ink-2 py-14 border-t border-white/10">
          <div className="max-w-[1100px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Left: upload + preview */}
            <div>
              <p className="font-mono2 text-gold uppercase tracking-[0.3em] text-xs mb-4 flex items-center gap-2">
                <ImagePlus className="w-4 h-4" /> Krok 1 — Fotka objektu
              </p>
              <div
                onClick={() => fileRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); pickFile(e.dataTransfer.files?.[0]); }}
                data-testid="vis-dropzone"
                className="border-2 border-dashed border-white/20 hover:border-gold/60 transition-colors cursor-pointer bg-ink flex items-center justify-center aspect-[4/3] overflow-hidden"
              >
                {preview ? (
                  <img src={preview} alt="Náhľad" className="w-full h-full object-cover" data-testid="vis-preview" />
                ) : (
                  <div className="text-center px-6">
                    <Upload className="w-10 h-10 text-gold mx-auto mb-3" />
                    <p className="text-white/80 font-semibold">Kliknite alebo presuňte fotku sem</p>
                    <p className="text-white/40 text-xs mt-1">JPG, PNG alebo WEBP · max 10 MB</p>
                  </div>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                data-testid="vis-file-input" onChange={(e) => pickFile(e.target.files?.[0])} />
              {preview && (
                <button onClick={() => fileRef.current?.click()} className="mt-3 text-xs text-white/50 hover:text-gold underline">
                  Zmeniť fotku
                </button>
              )}

              <div className="mt-6">
                <p className="text-white/50 text-xs uppercase tracking-widest font-mono2 mb-2">Typ objektu</p>
                <div className="grid grid-cols-3 gap-2" data-testid="vis-object-types">
                  {[["dom", "Rodinný dom"], ["bytovka", "Bytový dom"], ["budova", "Budova"]].map(([k, l]) => (
                    <button key={k} onClick={() => setObjectType(k)} data-testid={`vis-objtype-${k}`}
                      className={`py-2 text-sm border transition-colors ${objectType === k ? "border-gold bg-gold/10 text-gold" : "border-white/15 text-white/70 hover:border-white/40"}`}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: palette + colors + email */}
            <div className="space-y-6">
              <div>
                <p className="font-mono2 text-gold uppercase tracking-[0.3em] text-xs mb-4 flex items-center gap-2">
                  <Palette className="w-4 h-4" /> Krok 2 — Farby
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="vis-palettes">
                  {palettes.map((p) => (
                    <button key={p.name} onClick={() => setPalette(palette === p.name ? "" : p.name)}
                      data-testid={`vis-palette-${p.name}`}
                      className={`p-2 border text-left transition-colors ${palette === p.name ? "border-gold bg-gold/10" : "border-white/15 hover:border-white/40"}`}>
                      <div className="flex gap-1 mb-2">
                        {p.colors.map((c) => <span key={c} className="w-full h-5" style={{ background: c }} />)}
                      </div>
                      <span className="text-[11px] text-white/70 leading-tight block">{p.name}</span>
                    </button>
                  ))}
                </div>
                <input value={colors} onChange={(e) => setColors(e.target.value)} data-testid="vis-colors"
                  placeholder="Alebo napíšte želané farby (napr. svetlo sivá + antracitový sokel)"
                  className="w-full mt-3 bg-transparent border border-white/20 focus:border-gold outline-none text-white px-4 py-3 text-sm" />
                {swatchUrl && (
                  <a href={imgUrl(swatchUrl)} target="_blank" rel="noreferrer" data-testid="vis-swatch-download"
                    className="inline-flex items-center gap-2 mt-3 text-xs text-gold hover:text-gold-light underline">
                    Stiahnuť vzorkovník Ceresit (PDF)
                  </a>
                )}
              </div>

              <div>
                <p className="font-mono2 text-gold uppercase tracking-[0.3em] text-xs mb-4">Krok 3 — Kam poslať návrhy</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input value={name} onChange={(e) => setName(e.target.value)} data-testid="vis-name"
                    placeholder="Meno (nepovinné)" className="bg-transparent border border-white/20 focus:border-gold outline-none text-white px-4 py-3 text-sm" />
                  <input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="vis-email" type="email"
                    placeholder="Váš e-mail *" className="bg-transparent border border-white/20 focus:border-gold outline-none text-white px-4 py-3 text-sm" />
                </div>
                <label className="flex items-start gap-3 mt-4 cursor-pointer group" data-testid="vis-gallery-consent-label">
                  <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="vis-gallery-consent"
                    className="mt-0.5 w-4 h-4 shrink-0 accent-gold" />
                  <span className="text-white/60 text-xs leading-relaxed group-hover:text-white/80 transition-colors">
                    Súhlasím so zverejnením mojej vizualizácie vo <Link to="/galeria" className="text-gold underline hover:text-gold-light">verejnej galérii</Link> (bez mena a kontaktu). Zverejnenie podlieha schváleniu. (nepovinné)
                  </span>
                </label>
                <button onClick={generate} disabled={busy} data-testid="vis-generate"
                  className="w-full mt-4 bg-gold text-ink py-4 font-bold uppercase tracking-wider hover:bg-gold-light flex items-center justify-center gap-2 disabled:opacity-60">
                  {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Generujem 3 návrhy…</> : <><Wand2 className="w-4 h-4" /> Vytvoriť 3 návrhy zadarmo</>}
                </button>
                <p className="text-white/40 text-xs mt-3">
                  Vizualizácia je nezáväzná a bezplatná. Na e-mail vám pošleme návrhy aj možnosť získať presnú cenovú ponuku.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}

const shareUrl = SITE_URL + "/vizualizacia";
const shareText = "Pozri, ako môže vyzerať moja nová fasáda! Vytvor si vlastnú bezplatnú AI vizualizáciu na Zateplenia.eu";

const OBJ_TYPE_MAP = { dom: "family_house", bytovka: "apartment_building", budova: "building" };

const ResultView = ({ result, name, email, colors, palette, onReset }) => {
  const qp = new URLSearchParams();
  if (name) qp.set("name", name);
  if (email) qp.set("email", email);
  const mappedType = OBJ_TYPE_MAP[result.object_type] || "";
  if (mappedType) qp.set("type", mappedType);
  if (result.estimate) {
    qp.set("area", String(result.estimate.typical_area));
    qp.set("price", String(Math.round(result.estimate.typical_total)));
    qp.set("ppm", String(Math.round(result.estimate.price_per_m2)));
  }
  const colDesc = (colors || "").trim() || palette || "";
  if (colDesc) qp.set("note", `Farby z AI vizualizácie: ${colDesc}`);
  qp.set("src", "vizualizacia");
  const quoteUrl = `/objednavka?${qp.toString()}`;
  const shareWhatsApp = () => {
    track("visualize_share", "whatsapp");
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`, "_blank");
  };
  const shareFacebook = () => {
    track("visualize_share", "facebook");
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`, "_blank");
  };
  const nativeShare = async (url) => {
    track("visualize_share", "native");
    try {
      if (navigator.share) await navigator.share({ title: "Moja nová fasáda", text: shareText, url: imgUrl(url) });
      else { await navigator.clipboard.writeText(imgUrl(url)); toast.success("Odkaz na obrázok skopírovaný."); }
    } catch { /* user cancelled */ }
  };

  return (
    <section className="bg-ink-2 py-14 border-t border-white/10">
      <div className="max-w-[1200px] mx-auto px-5 md:px-10">
        <div className="flex items-center gap-2 text-gold mb-4">
          <Check className="w-5 h-5" />
          <p className="font-mono2 uppercase tracking-[0.3em] text-xs">Hotovo{name ? `, ${name}` : ""}!</p>
        </div>
        <h2 className="font-display text-3xl md:text-4xl text-white mb-2">Vaše 3 návrhy fasády</h2>
        <p className="text-white/60 mb-8 text-sm">Potiahnite posuvník a porovnajte pôvodný stav s návrhom. Kópiu sme poslali aj na váš e-mail.</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="vis-results">
          {(result.images || []).map((u, i) => (
            <div key={u} className="border border-white/10 overflow-hidden bg-ink" data-testid={`vis-result-${i}`}>
              <BeforeAfterSlider before={imgUrl(result.original)} after={imgUrl(u)} testid={`vis-slider-${i}`} afterLabel={`Návrh ${i + 1}`} />
              <div className="px-4 py-3 flex items-center justify-between">
                <span className="text-white/80 text-sm font-semibold">Návrh {i + 1}</span>
                <div className="flex items-center gap-3">
                  <a href={imgUrl(u)} target="_blank" rel="noreferrer" data-testid={`vis-download-${i}`} className="text-gold text-xs uppercase tracking-widest flex items-center gap-1 hover:text-gold-light"><Download className="w-3.5 h-3.5" /> Stiahnuť</a>
                  <button onClick={() => nativeShare(u)} data-testid={`vis-share-native-${i}`} className="text-white/60 hover:text-gold"><Share2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {result.estimate && (
          <div className="mt-8 border border-gold/40 bg-gold/5 p-6 md:p-8" data-testid="vis-estimate">
            <p className="font-mono2 text-gold uppercase tracking-[0.3em] text-xs mb-3">Orientačná cena zateplenia</p>
            <div className="flex flex-col md:flex-row md:items-end gap-4 md:gap-10">
              <div>
                <div className="font-display text-4xl md:text-5xl text-white">od {Math.round(result.estimate.price_per_m2).toLocaleString("sk-SK")} €<span className="text-xl text-white/50">/m²</span></div>
                <p className="text-white/50 text-sm mt-1">s DPH — materiál, práca, lešenie aj doprava</p>
              </div>
              <div className="md:border-l md:border-white/15 md:pl-10">
                <div className="font-display text-3xl md:text-4xl text-gold">~{Math.round(result.estimate.typical_total).toLocaleString("sk-SK")} €</div>
                <p className="text-white/50 text-sm mt-1">typický {result.estimate.object_label} (~{result.estimate.typical_area} m² fasády)</p>
              </div>
            </div>
            <p className="text-white/40 text-xs mt-4">Ide o orientačný odhad. Presnú cenu pre váš objekt spočítate v kalkulačke nižšie.</p>
          </div>
        )}


        {/* Share row */}
        <div className="mt-8 border border-white/10 bg-ink p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="vis-share-row">
          <p className="text-white/70 text-sm flex items-center gap-2"><Share2 className="w-4 h-4 text-gold" /> Páči sa vám výsledok? Zdieľajte ho s rodinou či susedmi.</p>
          <div className="flex gap-3">
            <button onClick={shareWhatsApp} data-testid="vis-share-whatsapp" className="flex items-center gap-2 bg-[#25D366] text-white px-5 py-2.5 text-sm font-bold hover:opacity-90">
              <i className="fa-brands fa-whatsapp text-lg" /> WhatsApp
            </button>
            <button onClick={shareFacebook} data-testid="vis-share-facebook" className="flex items-center gap-2 bg-[#1877F2] text-white px-5 py-2.5 text-sm font-bold hover:opacity-90">
              <i className="fa-brands fa-facebook-f text-lg" /> Facebook
            </button>
          </div>
        </div>

        <div className="mt-6 border border-gold/40 bg-gold/5 p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6" data-testid="vis-quote-cta">
          <div>
            <h3 className="font-display text-2xl text-white">Páči sa vám niektorý návrh?</h3>
            <p className="text-white/60 text-sm mt-1">Pošlite si nezáväznú cenovú ponuku — meno, e-mail, typ objektu, farby aj orientačnú cenu predvyplníme automaticky.</p>
          </div>
          <div className="flex gap-3 flex-wrap items-center">
            <Link to={quoteUrl} onClick={() => track("visualize_quote_click", colDesc || "none")} data-testid="vis-cta-quote"
              className="bg-gold text-ink px-6 py-3 font-bold uppercase tracking-wider hover:bg-gold-light flex items-center gap-2">
              Chcem cenovú ponuku <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/#kalkulacka" data-testid="vis-cta-calc" className="border border-white/25 text-white px-6 py-3 font-bold uppercase tracking-wider hover:border-gold hover:text-gold">
              Presná kalkulácia
            </Link>
            <button onClick={onReset} data-testid="vis-again" className="border border-white/25 text-white px-6 py-3 font-bold uppercase tracking-wider hover:border-gold hover:text-gold">
              Skúsiť ďalšie farby
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
